import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Card, Col, Descriptions, Empty, Input, Radio, Row, Space, Typography, Upload, message } from 'antd';
import { CameraOutlined, UploadOutlined } from '@ant-design/icons';

import { useAuth } from '../../components/common/security/AuthContext';
import { dccReadExif, type DccExifResponse } from '../../services/dccApi';
import Button from '../../components/common/Button';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

type InputMode = 'upload' | 'server';

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  if (Array.isArray(value) || typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const CorExifReader: React.FC = () => {
  const { token } = useAuth();
  const [inputMode, setInputMode] = useState<InputMode>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [serverFilePath, setServerFilePath] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DccExifResponse | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingOpenPicker, setPendingOpenPicker] = useState(false);

  const canSubmit = useMemo(() => {
    if (inputMode === 'upload') {
      return !!selectedFile;
    }
    return !!serverFilePath.trim();
  }, [inputMode, selectedFile, serverFilePath]);

  const tagsJson = useMemo(() => {
    if (!result?.tags) {
      return '';
    }
    try {
      return JSON.stringify(result.tags, null, 2);
    } catch {
      return String(result.tags);
    }
  }, [result]);

  const applySelectedFile = (file: File) => {
    setSelectedFile(file);
    setResult(null);

    if (previewUrl) {
      try {
        URL.revokeObjectURL(previewUrl);
      } catch {}
    }

    if (file.type.startsWith('image/')) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }
  };

  const handleReadExif = async () => {
    if (!token) {
      message.error('Oturum bulunamadı. Lütfen tekrar giriş yapın.');
      return;
    }

    if (inputMode === 'upload' && !selectedFile) {
      message.warning('Lütfen bir fotoğraf seçin.');
      return;
    }

    if (inputMode === 'server' && !serverFilePath.trim()) {
      message.warning('Lütfen sunucu dosya yolunu girin.');
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const response = await dccReadExif(
        inputMode === 'upload'
          ? { file: selectedFile || undefined }
          : { filePath: serverFilePath.trim() },
        token
      );
      setResult(response);
      message.success('EXIF bilgileri okundu.');
    } catch (error: any) {
      message.error(error?.message || 'EXIF bilgileri okunamadı.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch {}
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    if (inputMode === 'server') {
      setSelectedFile(null);
      if (previewUrl) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch {}
      }
      setPreviewUrl(null);
    }
  }, [inputMode, previewUrl]);

  useEffect(() => {
    if (inputMode === 'upload' && pendingOpenPicker) {
      setTimeout(() => fileInputRef.current?.click(), 0);
      setPendingOpenPicker(false);
    }
  }, [inputMode, pendingOpenPicker]);

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={2} style={{ marginBottom: 8 }}>
            Fotoğraf EXIF Okuyucu
          </Title>
          <Paragraph style={{ marginBottom: 0 }}>
            Fotoğrafların kamera, çekim zamanı, boyut ve GPS gibi EXIF metadata alanlarını okur.
            Yerel dosya yükleyebilir veya `5006` servisindeki `/root/uploads` altından bir görseli
            yol vererek sorgulayabilirsiniz.
          </Paragraph>
        </div>

        <Alert
          type="info"
          showIcon
          message="Seçilen kütüphane: ExifRead"
          description="GitHub taramasında `ExifRead`, mevcut Python/Flask + VPS servis yapımıza en uygun seçenek olarak öne çıktı: saf Python, hafif, bağımlılık yükü düşük ve JPEG/PNG/WEBP/HEIC/RAW gibi formatları okuyabiliyor."
        />

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={10}>
            <Card title="Kaynak Fotoğraf">
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Radio.Group
                  value={inputMode}
                  onChange={(e) => {
                    const value = e.target.value as InputMode;
                    setInputMode(value);
                    if (value === 'upload') {
                      setPendingOpenPicker(true);
                    }
                  }}
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button
                    value="upload"
                    onClick={() => {
                      if (inputMode === 'upload') {
                        fileInputRef.current?.click();
                      }
                    }}
                  >
                    Yerel Dosya
                  </Radio.Button>
                  <Radio.Button value="server">Sunucu Dosya Yolu</Radio.Button>
                </Radio.Group>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.tif,.tiff,.png,.webp,.heic,.jxl,.cr2,.nef,.arw,.dng,.raf,.rw2"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    if (file) {
                      applySelectedFile(file);
                      try {
                        e.target.value = '';
                      } catch {}
                    }
                  }}
                />

                {inputMode === 'upload' ? (
                  <Upload.Dragger
                    accept=".jpg,.jpeg,.tif,.tiff,.png,.webp,.heic,.jxl,.cr2,.nef,.arw,.dng,.raf,.rw2"
                    showUploadList={false}
                    maxCount={1}
                    beforeUpload={(file) => {
                      applySelectedFile(file as File);
                      return false;
                    }}
                    onChange={(info) => {
                      const file = (info?.file?.originFileObj || info?.file) as File | undefined;
                      if (file) {
                        applySelectedFile(file);
                      }
                    }}
                    disabled={loading}
                  >
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="preview"
                        style={{
                          maxHeight: 260,
                          width: 'auto',
                          maxWidth: '100%',
                          display: 'block',
                          margin: '0 auto',
                        }}
                      />
                    ) : (
                      <>
                        <p className="ant-upload-drag-icon">
                          <UploadOutlined />
                        </p>
                        <p className="ant-upload-text">Fotoğraf seçin veya buraya bırakın</p>
                        <p className="ant-upload-hint">
                          Desteklenen formatlar: JPG, TIFF, PNG, WEBP, HEIC, JXL ve yaygın RAW
                          uzantıları
                        </p>
                      </>
                    )}
                  </Upload.Dragger>
                ) : (
                  <Input
                    value={serverFilePath}
                    onChange={(e) => setServerFilePath(e.target.value)}
                    placeholder="Örn: temp/foto.jpg veya dcc/arsiv/saha_01.heic"
                    disabled={loading}
                  />
                )}

                {inputMode === 'upload' && selectedFile && (
                  <Text type="secondary">Seçilen dosya: {selectedFile.name}</Text>
                )}

                <Button
                  type="primary"
                  icon={<CameraOutlined />}
                  onClick={handleReadExif}
                  loading={loading}
                  disabled={!canSubmit}
                >
                  EXIF Bilgilerini Oku
                </Button>
              </Space>
            </Card>
          </Col>

          <Col xs={24} lg={14}>
            <Card title="EXIF Sonucu">
              {result?.success ? (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Descriptions size="small" bordered column={2}>
                    <Descriptions.Item label="Kütüphane">
                      {result.library || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Kaynak">
                      {result.source_type || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Dosya">
                      {result.file_name || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Tag sayısı">
                      {result.tag_count ?? 0}
                    </Descriptions.Item>
                    <Descriptions.Item label="Marka">
                      {formatValue(result.summary?.make)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Model">
                      {formatValue(result.summary?.model)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Çekim zamanı">
                      {formatValue(result.summary?.captured_at)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Yazılım">
                      {formatValue(result.summary?.software)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Boyut">
                      {`${formatValue(result.summary?.width)} x ${formatValue(result.summary?.height)}`}
                    </Descriptions.Item>
                    <Descriptions.Item label="Orientation">
                      {formatValue(result.summary?.orientation)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Lens">
                      {formatValue(result.summary?.lens_model)}
                    </Descriptions.Item>
                    <Descriptions.Item label="ISO">
                      {formatValue(result.summary?.iso)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Diyafram">
                      {formatValue(result.summary?.f_number)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Enstantane">
                      {formatValue(result.summary?.exposure_time)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Odak uzaklığı">
                      {formatValue(result.summary?.focal_length)}
                    </Descriptions.Item>
                    <Descriptions.Item label="GPS">
                      {result.summary?.latitude != null && result.summary?.longitude != null
                        ? `${result.summary.latitude}, ${result.summary.longitude}`
                        : '-'}
                    </Descriptions.Item>
                  </Descriptions>

                  <div>
                    <Text strong>Ham EXIF JSON</Text>
                    <TextArea
                      value={tagsJson}
                      readOnly
                      autoSize={{ minRows: 14, maxRows: 24 }}
                      placeholder="Okunan tag'ler burada görünecek."
                    />
                  </div>
                </Space>
              ) : result && !result.success ? (
                <Alert
                  type="warning"
                  showIcon
                  message="EXIF bilgisi okunamadı"
                  description={result.message || 'Servis geçerli bir metadata döndürmedi.'}
                />
              ) : (
                <Empty description="Henüz EXIF sonucu yok." />
              )}
            </Card>
          </Col>
        </Row>
      </Space>
    </div>
  );
};

export default CorExifReader;
